import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  Signal,
  viewChild,
  WritableSignal
} from '@angular/core';

export const TIME_WHEEL_ITEM_HEIGHT: number = 40;

@Component({
  selector: 'sz-time-wheel',
  templateUrl: './sz-time-wheel.component.html',
  styleUrls: ['./sz-time-wheel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SzTimeWheelComponent implements AfterViewInit {
  readonly value: InputSignal<string | null> = input<string | null>(null);
  readonly minuteStep: InputSignal<number> = input<number>(5);

  @Output() readonly valueChange: EventEmitter<string> = new EventEmitter<string>();

  private readonly hoursCol: Signal<ElementRef<HTMLElement> | undefined> =
    viewChild<ElementRef<HTMLElement>>('hoursCol');
  private readonly minutesCol: Signal<ElementRef<HTMLElement> | undefined> =
    viewChild<ElementRef<HTMLElement>>('minutesCol');

  readonly hours: ReadonlyArray<number> = Array.from({ length: 24 }, (_: unknown, i: number): number => i);

  readonly minutes: Signal<ReadonlyArray<number>> = computed((): ReadonlyArray<number> => {
    const step: number = Math.max(1, this.minuteStep());
    return Array.from({ length: Math.ceil(60 / step) }, (_: unknown, i: number): number => i * step);
  });

  private readonly selectedHour: WritableSignal<number> = signal<number>(12);
  private readonly selectedMinute: WritableSignal<number> = signal<number>(0);

  private hoursSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private minutesSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private viewReady: boolean = false;

  constructor() {
    // Synchronizacja, gdy wartość przyjdzie z zewnątrz.
    effect((): void => {
      const parsed: { hour: number; minute: number } = this.parse(this.value());
      this.selectedHour.set(parsed.hour);
      this.selectedMinute.set(this.snapMinute(parsed.minute));
      if (this.viewReady) {
        this.scrollToSelected(false);
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scrollToSelected(false);
  }

  onHoursScroll(): void {
    if (this.hoursSettleTimer !== null) {
      clearTimeout(this.hoursSettleTimer);
    }
    this.hoursSettleTimer = setTimeout((): void => this.settleHours(), 120);
  }

  onMinutesScroll(): void {
    if (this.minutesSettleTimer !== null) {
      clearTimeout(this.minutesSettleTimer);
    }
    this.minutesSettleTimer = setTimeout((): void => this.settleMinutes(), 120);
  }

  private settleHours(): void {
    const el: HTMLElement | undefined = this.hoursCol()?.nativeElement;
    if (el === undefined) {
      return;
    }
    const index: number = this.clamp(Math.round(el.scrollTop / TIME_WHEEL_ITEM_HEIGHT), 0, this.hours.length - 1);
    const hour: number = this.hours[index];
    if (hour !== this.selectedHour()) {
      this.selectedHour.set(hour);
      this.emit();
    }
  }

  private settleMinutes(): void {
    const el: HTMLElement | undefined = this.minutesCol()?.nativeElement;
    if (el === undefined) {
      return;
    }
    const list: ReadonlyArray<number> = this.minutes();
    const index: number = this.clamp(Math.round(el.scrollTop / TIME_WHEEL_ITEM_HEIGHT), 0, list.length - 1);
    const minute: number = list[index];
    if (minute !== this.selectedMinute()) {
      this.selectedMinute.set(minute);
      this.emit();
    }
  }

  private scrollToSelected(smooth: boolean): void {
    const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';
    const hoursEl: HTMLElement | undefined = this.hoursCol()?.nativeElement;
    const minutesEl: HTMLElement | undefined = this.minutesCol()?.nativeElement;
    const hourIndex: number = this.hours.indexOf(this.selectedHour());
    const minuteIndex: number = this.minutes().indexOf(this.selectedMinute());
    hoursEl?.scrollTo({ top: Math.max(0, hourIndex) * TIME_WHEEL_ITEM_HEIGHT, behavior });
    minutesEl?.scrollTo({ top: Math.max(0, minuteIndex) * TIME_WHEEL_ITEM_HEIGHT, behavior });
  }

  private emit(): void {
    this.valueChange.emit(`${this.pad(this.selectedHour())}:${this.pad(this.selectedMinute())}`);
  }

  private parse(value: string | null): { hour: number; minute: number } {
    if (value === null || !/^\d{1,2}:\d{1,2}$/.test(value)) {
      return { hour: 12, minute: 0 };
    }
    const [h, m]: number[] = value.split(':').map((p: string): number => Number.parseInt(p, 10));
    return { hour: this.clamp(h, 0, 23), minute: this.clamp(m, 0, 59) };
  }

  private snapMinute(minute: number): number {
    const list: ReadonlyArray<number> = this.minutes();
    return list.reduce((best: number, current: number): number =>
      Math.abs(current - minute) < Math.abs(best - minute) ? current : best
    );
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
